import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Flame, Plus, Trash2, Phone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  full:     { label: "Full",     chip: "bg-hearth-sage/15 text-hearth-sageDark", dot: "bg-hearth-sage" },
  in_use:   { label: "In use",   chip: "bg-hearth-amber/20 text-[#7A5320]",      dot: "bg-hearth-amber" },
  empty:    { label: "Empty",    chip: "bg-hearth-coral/15 text-hearth-coral",   dot: "bg-hearth-coral" },
  on_order: { label: "On order", chip: "bg-hearth-clay/12 text-hearth-clayDark", dot: "bg-hearth-clay" },
};

export default function GasBottles() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const blank = {
    label: "", size_kg: "", supplier_id: "", supplier_name: "", supplier_phone: "",
    last_refill_date: "", typical_days_between_refills: "", status: "in_use", notes: "",
  };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    const [{ data: items }, { data: allVendors }] = await Promise.all([
      api.get("/gas-bottles"),
      api.get("/vendors"),
    ]);
    setItems(items);
    // Only vendors typed as 'Gas supplier' show up here — keeps them separate from the
    // general Vendors list where you keep supermarkets, butchers, etc.
    setVendors(allVendors.filter((v) => v.type === "Gas supplier"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.label.trim()) return toast.error("Give this bottle a label (e.g. 'Kitchen 9kg')");
    try {
      const payload = {
        ...form,
        size_kg: form.size_kg ? parseFloat(form.size_kg) : null,
        typical_days_between_refills: form.typical_days_between_refills ? parseInt(form.typical_days_between_refills, 10) : null,
        supplier_id: form.supplier_id || null,
      };
      if (editId) await api.put(`/gas-bottles/${editId}`, payload);
      else await api.post("/gas-bottles", payload);
      setOpen(false); setForm(blank); setEditId(null); toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (it) => { setForm({ ...blank, ...it }); setEditId(it.id); setOpen(true); };
  const remove = async (id) => { await api.delete(`/gas-bottles/${id}`); load(); };

  const markStatus = async (it, status) => {
    const payload = { ...it, status };
    if (status === "full") payload.last_refill_date = new Date().toISOString().slice(0, 10);
    await api.put(`/gas-bottles/${it.id}`, payload);
    toast.success(`Marked as ${STATUS_META[status].label.toLowerCase()}`);
    load();
  };

  // Compute days until next likely refill and whether it's overdue
  const enriched = useMemo(() => items.map((it) => {
    const lr = it.last_refill_date ? new Date(it.last_refill_date) : null;
    const days = it.typical_days_between_refills;
    let daysLeft = null;
    if (lr && days) {
      const next = new Date(lr.getTime() + days * 86400000);
      daysLeft = Math.round((next - new Date()) / 86400000);
    }
    return { ...it, daysLeft };
  }), [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Household · QLD gas"
        title="Gas bottles"
        subtitle="Track your LPG bottles, log refills and call the supplier when one runs low."
        action={
          <Button data-testid="gas-add-button" onClick={() => { setEditId(null); setForm(blank); setOpen(true); }}
            className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10">
            <Plus size={15} className="mr-1.5" /> Add bottle
          </Button>
        }
      />

      {enriched.length === 0 ? (
        <EmptyState icon={Flame} title="No bottles tracked yet." message="Add your BBQ or kitchen bottle to keep tabs on refills." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {enriched.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.in_use;
            const runningLow = it.daysLeft != null && it.daysLeft <= 3;
            return (
              <div key={it.id} data-testid={`gas-row-${it.id}`} className="warm-card p-5 fade-up">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-hearth-clay/12 text-hearth-clayDark grid place-items-center shrink-0">
                    <Flame size={18} strokeWidth={1.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-xl text-hearth-ink truncate">{it.label}</h3>
                      {it.size_kg && <span className="chip bg-hearth-stone text-hearth-mute font-mono">{it.size_kg}kg</span>}
                      <span className={`chip ${meta.chip} inline-flex items-center gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {meta.label}
                      </span>
                    </div>
                    {it.supplier_name && (
                      <p className="text-sm text-hearth-mute mt-1">Supplier: <span className="text-hearth-ink">{it.supplier_name}</span></p>
                    )}
                    {it.last_refill_date && (
                      <p className="text-xs text-hearth-mute mt-1">Last refilled {fmtDate(it.last_refill_date)}</p>
                    )}
                    {it.daysLeft != null && (
                      <p className={`text-xs mt-1 inline-flex items-center gap-1 ${runningLow ? "text-hearth-coral" : "text-hearth-mute"}`}>
                        {runningLow && <AlertTriangle size={11} />}
                        {it.daysLeft > 0 ? `~${it.daysLeft} days until likely refill` : it.daysLeft === 0 ? "Refill likely today" : `Refill ${Math.abs(it.daysLeft)}d overdue`}
                      </p>
                    )}
                    {it.notes && <p className="text-xs text-hearth-mute mt-2 italic">{it.notes}</p>}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-hearth-line/70 flex flex-wrap gap-2">
                  {it.status !== "full" && (
                    <Button data-testid={`gas-mark-full-${it.id}`} onClick={() => markStatus(it, "full")}
                      className="rounded-full bg-hearth-sage hover:bg-hearth-sageDark text-white h-8 px-3 text-xs">Just refilled</Button>
                  )}
                  {it.status !== "empty" && it.status !== "on_order" && (
                    <Button data-testid={`gas-mark-empty-${it.id}`} onClick={() => markStatus(it, "empty")} variant="ghost"
                      className="rounded-full h-8 px-3 text-xs border border-hearth-coral/30 text-hearth-coral hover:bg-hearth-coral/10">Mark empty</Button>
                  )}
                  {it.status === "empty" && (
                    <Button data-testid={`gas-mark-order-${it.id}`} onClick={() => markStatus(it, "on_order")}
                      className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white h-8 px-3 text-xs">Ordered</Button>
                  )}
                  {it.supplier_phone && <a href={`tel:${it.supplier_phone}`} className="pill-btn text-xs border border-hearth-line bg-hearth-paper hover:bg-hearth-line/50 inline-flex items-center gap-1"><Phone size={11} /> Call supplier</a>}
                  <div className="ml-auto flex gap-1">
                    <Button variant="ghost" data-testid={`gas-edit-${it.id}`} onClick={() => startEdit(it)} className="rounded-full h-8 px-3 text-xs">Edit</Button>
                    <Button variant="ghost" data-testid={`gas-delete-${it.id}`} onClick={() => remove(it.id)} className="rounded-full h-8 px-2 text-hearth-coral hover:bg-hearth-coral/10"><Trash2 size={13} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editId ? "Edit bottle" : "Add gas bottle"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="caption text-hearth-mute">Label</Label>
              <Input data-testid="gas-form-label" className="input-warm mt-1.5 h-11" placeholder="e.g. Kitchen 9kg / BBQ patio" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Size (kg)</Label>
              <Input type="number" step="0.1" className="input-warm mt-1.5 h-11" value={form.size_kg || ""} onChange={(e) => setForm({ ...form, size_kg: e.target.value })} placeholder="4, 9, 45…" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="gas-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="in_use">In use</SelectItem>
                  <SelectItem value="empty">Empty</SelectItem>
                  <SelectItem value="on_order">On order</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Gas supplier</Label>
              <Select value={form.supplier_id || "__none"} onValueChange={(v) => {
                if (v === "__none") { setForm({ ...form, supplier_id: "" }); return; }
                const v_ = vendors.find((x) => x.id === v);
                setForm({ ...form, supplier_id: v, supplier_name: v_?.name || "", supplier_phone: v_?.phone || "" });
              }}>
                <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="gas-form-supplier"><SelectValue placeholder={vendors.length === 0 ? "No gas suppliers yet" : "Choose supplier…"} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— type manually below —</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {vendors.length === 0 && (
                <p className="caption mt-1 text-hearth-mute">
                  Tip: add gas suppliers on the{" "}
                  <a href="/vendors" className="text-hearth-clay hover:underline">Vendors page</a>{" "}
                  with type &ldquo;Gas supplier&rdquo; — they&apos;ll show up here only.
                </p>
              )}
            </div>
            <div>
              <Label className="caption text-hearth-mute">Supplier phone</Label>
              <Input className="input-warm mt-1.5 h-11" value={form.supplier_phone || ""} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} placeholder="+61 1300 XXX XXX" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Last refilled</Label>
              <Input type="date" className="input-warm mt-1.5 h-11" value={form.last_refill_date || ""} onChange={(e) => setForm({ ...form, last_refill_date: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Typical days between refills</Label>
              <Input type="number" className="input-warm mt-1.5 h-11" value={form.typical_days_between_refills || ""} onChange={(e) => setForm({ ...form, typical_days_between_refills: e.target.value })} placeholder="e.g. 45" />
            </div>
            <div className="md:col-span-2">
              <Label className="caption text-hearth-mute">Notes</Label>
              <Textarea className="input-warm mt-1.5" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="gas-form-save" onClick={save} className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
